/**
 * TSL node tree → readable TSL source (best-effort reconstruction).
 *
 * Walks a `NodeMaterial`'s assigned `*Node` roots (the same entry points the
 * graph builder uses — see `nodegraph.tsl.ts`) and emits an approximation of
 * the TSL JavaScript that would produce that tree:
 *
 *     // MeshStandardNodeMaterial "MyMat" — TSL reconstructed from the live node graph
 *     const roughness = uniform(0.5);
 *     material.colorNode = mix(color('#ff0000'), color('#00ff00'), roughness);
 *
 * Same robustness rules as the graph builder: pure duck typing (the nodes come
 * from the inspected page's three.js — any version, another realm), never
 * `instanceof`, and everything guarded. Shared subtrees (a node referenced more
 * than once) hoist into `const` temps so the fan-out stays visible; uniforms
 * and named `.toVar()`s always hoist, mirroring how TSL is actually written.
 * The reconstruction is lossy by design — compiler-inserted wrappers are elided
 * and unknown node types fall back to `<typeName>(…children)`.
 */

import type { Material } from "three";
import { isNodeLike, type NodeLike } from "./helper.nodes.js";
import { getChildNodes, getMaterialNodeRoots } from "./nodegraph.tsl.js";

/**
 * Expands an anonymous TSL `Fn` call node into its body (the call's inputs
 * already substituted). Supplied by callers that hold a live render context
 * (see `expandFnCall` in nodegraph.tsl.ts); returns null when the body can't
 * be built. Without an expander, anonymous Fn calls stay stubbed.
 */
export type FnExpander = (fnCallNode: NodeLike) => NodeLike | null;

/** Hard cap on emitted nodes to guard against pathological graphs. */
const MAX_EMIT_NODES = 3000;
/** Recursion cap — emission is recursive, so depth must stay far below the JS
 *  stack limit (a 4000-deep node chain would otherwise overflow mid-emit). */
const MAX_EMIT_DEPTH = 200;
/** Expressions longer than this hoist into a variable — hand-written TSL is a
 *  sequence of short statements, not one deeply nested expression (which
 *  Prettier then folds into an unreadable indentation staircase). */
const MAX_INLINE_LENGTH = 64;
/** How far a variable name is searched up a single-consumer chain (the chain
 *  base carries the name of what the chain BECOMES, e.g. `alpha`). */
const MAX_NAME_SEARCH_DEPTH = 6;
/** How deep anonymous `Fn` bodies expand into each other. Each level RUNS the
 *  inspected page's closure (see `expandFnCall`), so this is a cost + runaway
 *  guard, not just a stack guard — nested helper Fns rarely go beyond a few. */
const MAX_FN_EXPAND_DEPTH = 6;

/** TSL identifiers the emitted code CALLS — inferred variable names must not
 *  shadow them (`const color = …` would break a later `color('#fff')`). */
const RESERVED_TSL_NAMES = [
    "float", "int", "uint", "bool", "vec2", "vec3", "vec4", "color",
    "texture", "uniform", "attribute", "uv", "fn", "reference", "material",
];

/** Root-slot variable names that would collide with a reserved call name. */
const ROOT_NAME_ALIASES: Record<string, string> = {
    colorNode: "baseColor",
};

/** TSL chain-method names for operator symbols (`a.add(b)`, `a.lessThan(b)`). */
const OPERATOR_METHODS: Record<string, string> = {
    "+": "add", "-": "sub", "*": "mul", "/": "div", "%": "mod",
    "==": "equal", "!=": "notEqual", "<": "lessThan", ">": "greaterThan",
    "<=": "lessThanEqual", ">=": "greaterThanEqual",
    "&&": "and", "||": "or", "^^": "xor",
    "&": "bitAnd", "|": "bitOr", "^": "bitXor",
    "<<": "shiftLeft", ">>": "shiftRight",
};
/** Unary operators (single operand) → chain methods. */
const UNARY_METHODS: Record<string, string> = {
    "!": "not", "~": "bitNot", "-": "negate",
};

const asRecord = (node: NodeLike) => node as unknown as Record<string, unknown>;
const typeName = (node: NodeLike): string => (typeof node.type === "string" && node.type ? node.type : "Node");
const getOp = (node: NodeLike): string | null => {
    const op = asRecord(node).op;
    return typeof op === "string" ? op : null;
};

/** True when the material has at least one TSL node root to emit from. */
export function hasTslCode(material: Material): boolean {
    try {
        return getMaterialNodeRoots(material).length > 0;
    } catch {
        return false;
    }
}

/**
 * Emits TSL source for every `*Node` root assigned on the material, or null
 * when there are none. Never throws — a node that resists introspection emits
 * as a commented placeholder instead of failing the whole document.
 */
export function emitMaterialTsl(material: Material, expandFn?: FnExpander): string | null {
    const roots = getMaterialNodeRoots(material);
    if (!roots.length) return null;
    const emitter = new TslEmitter(expandFn);
    // Count ALL roots first: a node shared across roots must be known as shared
    // (→ hoisted) before the first root inlines it. Root roles feed the
    // usage-based variable naming ("colorNode" → a chain named after it).
    for (const { key, node } of roots) {
        emitter.noteRoot(node, key);
        emitter.count(node);
    }
    const assignments: string[] = [];
    for (const { key, node } of roots) {
        assignments.push(`material.${key} = ${emitter.emit(node)};`);
    }

    const m = material as { type?: string; name?: string };
    const title = `${m.type ?? "NodeMaterial"}${m.name ? ` "${m.name}"` : ""}`;
    return assemble(emitter, title, assignments);
}

/**
 * Emits TSL source for a single node tree — e.g. a TSL `Fn` body expanded by a
 * node-graph drill-down (`expandFnCall`). The tree's root value is the Fn's
 * return value, so the body reads as `return <expr>;`.
 */
export function emitNodeTsl(root: NodeLike, title: string, expandFn?: FnExpander): string | null {
    if (!isNodeLike(root)) return null;
    const emitter = new TslEmitter(expandFn);
    emitter.noteRoot(root, "resultNode");
    emitter.count(root);
    return assemble(emitter, title, [`return ${emitter.emit(root)};`]);
}

function assemble(emitter: TslEmitter, title: string, statements: string[]): string {
    const parts: string[] = [`// ${title} — TSL reconstructed from the live node graph`];
    if (emitter.truncated) parts.push("// … truncated: the graph is too large/deep to emit fully");
    if (emitter.declarations.length) parts.push("", ...emitter.declarations);
    parts.push("", ...statements);
    return parts.join("\n");
}

class TslEmitter {
    /** Hoisted `const` lines, in dependency order (emitted before first use). */
    readonly declarations: string[] = [];
    truncated = false;

    /** uuid → reference count (from the counting pre-pass, across all roots). */
    private readonly _refs = new Map<string, number>();
    /** uuid → every `{parent, property}` referencing it (feeds name inference). */
    private readonly _parents = new Map<string, { parent: NodeLike; property: string }[]>();
    /** uuid → the material slot key it's assigned to (e.g. "colorNode"). */
    private readonly _rootRoles = new Map<string, string>();
    /** uuid → hoisted variable name (once a shared node is declared). */
    private readonly _vars = new Map<string, string>();
    /** variable name → uuid of the node it holds (for mutation folding). */
    private readonly _varUuids = new Map<string, string>();
    /** Variables a chain may fold into as a mutation — expression variables
     *  only. A pristine `const u = uniform(2)` must never end up holding a
     *  whole chain (and losing its meaning). */
    private readonly _foldable = new Set<string>();
    /** variable name → index of its `const` line in {@link declarations}. */
    private readonly _declIndex = new Map<string, number>();
    /** uuids currently being emitted — re-entry means a cycle. */
    private readonly _emitting = new Set<string>();
    private readonly _usedNames = new Set<string>(RESERVED_TSL_NAMES);
    private readonly _counted = new Set<string>();
    private _emitted = 0;
    private _depth = 0;
    /** How many anonymous Fn bodies deep we currently are (see MAX_FN_EXPAND_DEPTH). */
    private _fnDepth = 0;

    /** Optional Fn-body expander (Pro): inlines anonymous `Fn` calls instead of
     *  stubbing them. Undefined keeps the free-tier behavior (a stub comment). */
    constructor(private readonly _expandFn?: FnExpander) {}

    /** Records which material slot a root feeds — the strongest naming signal. */
    noteRoot(node: NodeLike, key: string): void {
        const inner = this._unwrapAnonymousVar(node);
        if (!this._rootRoles.has(inner.uuid)) this._rootRoles.set(inner.uuid, key);
    }

    /** Pre-pass: count references below `node` (call for every root FIRST). */
    count(node: NodeLike): void {
        this._countRefs(node, this._counted);
    }

    emit(node: NodeLike): string {
        return this._expr(node, /* isRoot */ true);
    }

    // #region reference counting (decides what hoists into a temp)
    private _countRefs(node: NodeLike, visited: Set<string>, depth = 0): void {
        if (depth >= MAX_EMIT_DEPTH) return;
        const inner = this._unwrapAnonymousVar(node);
        this._refs.set(inner.uuid, (this._refs.get(inner.uuid) ?? 0) + 1);
        if (visited.has(inner.uuid)) return;
        visited.add(inner.uuid);
        try {
            for (const { property, child } of getChildNodes(inner)) {
                const childInner = this._unwrapAnonymousVar(child);
                let entries = this._parents.get(childInner.uuid);
                if (!entries) this._parents.set(childInner.uuid, entries = []);
                entries.push({ parent: inner, property });
                this._countRefs(child, visited, depth + 1);
            }
        } catch { /* host node with hostile getters — leave as leaf */ }
    }
    // #endregion

    /** Anonymous VarNodes are compiler reuse markers — show the wrapped node.
     *  Named vars stay (they hoist as `.toVar()` consts). Iterative + seen-set
     *  so hostile var chains can't recurse or loop. */
    private _unwrapAnonymousVar(node: NodeLike): NodeLike {
        let current = node;
        const seen = new Set<string>();
        for (;;) {
            const r = asRecord(current);
            if (!r.isVarNode || !isNodeLike(r.node) || current.name || seen.has(current.uuid)) return current;
            seen.add(current.uuid);
            current = r.node;
        }
    }

    private _expr(input: NodeLike, isRoot = false): string {
        const node = this._unwrapAnonymousVar(input);

        const hoisted = this._vars.get(node.uuid);
        if (hoisted) return hoisted;
        if (this._emitting.has(node.uuid)) return "/* circular */ null";
        if (++this._emitted > MAX_EMIT_NODES || this._depth >= MAX_EMIT_DEPTH) {
            this.truncated = true;
            return "/* … */ null";
        }

        this._emitting.add(node.uuid);
        this._depth++;
        let code: string;
        try {
            code = this._build(node);
        } catch {
            code = `/* unreadable ${typeName(node)} */ null`;
        } finally {
            this._emitting.delete(node.uuid);
            this._depth--;
        }

        // Hoist into a variable so the emitted code mirrors hand-written TSL —
        // short statements, not one nested expression:
        // - shared subtrees & named vars: fan-out stays visible
        // - uniforms: always declared up front (that's how TSL is written)
        // - LONG expressions: flattened into steps (a root assignment is already
        //   its own statement, so size alone never hoists a root).
        // Trivial literals stay inline even when shared — a temp is only noise.
        const r = asRecord(node);
        const named = r.isVarNode === true || r.isUniformNode === true || typeName(node) === "UniformNode";
        const shared = (this._refs.get(node.uuid) ?? 0) > 1 && !this._isTrivial(node);
        const long = !isRoot && code.length > MAX_INLINE_LENGTH && this._hasChildren(node);
        if (named || shared || long) {
            const name = this._declare(node, code);
            this._vars.set(node.uuid, name);
            return name;
        }
        return code;
    }

    private _hasChildren(node: NodeLike): boolean {
        for (const _ of safeChildren(node)) return true;
        return false;
    }

    /** First node-valued child (for transparent wrappers like SubBuildNode). */
    private _firstChild(node: NodeLike): NodeLike | null {
        for (const { child } of safeChildren(node)) return child;
        return null;
    }

    /** Small leaf literals that read better inline than as a temp. */
    private _isTrivial(node: NodeLike): boolean {
        const r = asRecord(node);
        if (r.isConstNode === true || typeName(node) === "ConstNode") return true;
        const t = typeName(node);
        return t === "AttributeNode" || t === "UVNode";
    }

    // #region per-kind expression builders
    private _build(node: NodeLike): string {
        const r = asRecord(node);
        const t = typeName(node);

        // StackNode: an Fn body / scope surfaced by expansion. It EVALUATES to a
        // single value — its explicit `outputNode`, else the last statement — so
        // emit that, not the whole statement list. `.nodes` (the statements) and
        // `.parent` (the enclosing scope, an empty trailing `stack()`) are the
        // machinery `getChildNodes` would otherwise dump as `stack(a, b, …)`.
        if (r.isStackNode === true || t === "StackNode") {
            const out = isNodeLike(r.outputNode) ? r.outputNode : lastNodeIn(r.nodes);
            return out ? this._expr(out) : "/* empty stack */ null";
        }

        // Sub-build wrapper — from `.toVarying(getSubBuildProperty(…))` inside
        // expanded built-in Fn bodies; three tags a node for one build stage.
        // `subBuild(x)` is not writable TSL. Transparent: emit the wrapped node
        // so `varying(subBuild(x))` reads as `varying(x)`. Matched by the SAME
        // name the generic renderer would print (the concrete class name varies
        // across three versions — hardcoding "SubBuildNode" missed it live).
        if (lowerFirst(stripNodeSuffix(t)) === "subBuild" || r.method === "subBuild") {
            const inner = isNodeLike(r.node) ? r.node : isNodeLike(r.aNode) ? r.aNode : this._firstChild(node);
            if (inner) return this._expr(inner);
        }

        // Named VarNode: emit the wrapped expression + `.toVar()`.
        if (r.isVarNode && isNodeLike(r.node)) {
            return `${this._expr(r.node)}.toVar()`;
        }

        // Operators: binary chains (`a.mul(b)`) and unaries (`x.negate()`).
        const op = getOp(node);
        if (op) {
            const a = isNodeLike(r.aNode) ? r.aNode : null;
            const b = isNodeLike(r.bNode) ? r.bNode : null;
            if (a && b) {
                const method = OPERATOR_METHODS[op] ?? `op(${JSON.stringify(op)})`;
                return `${this._callable(this._expr(a))}.${method}(${this._expr(b)})`;
            }
            if (a) {
                const method = UNARY_METHODS[op] ?? OPERATOR_METHODS[op];
                if (method) return `${this._callable(this._expr(a))}.${method}()`;
            }
            return this._generic(node);
        }

        // Math nodes carry the TSL function name in `.method` (mix, clamp, sin…).
        // Chained off the first operand (`x.pow(3)`, `a.mix(b, c)`) — that's how
        // TSL is written, and it lets chains fold into variable mutations.
        const method = r.method;
        if (typeof method === "string" && method) {
            const a = isNodeLike(r.aNode) ? r.aNode : null;
            const rest = ["bNode", "cNode"]
                .map(k => r[k])
                .filter(isNodeLike)
                .map(n => this._expr(n));
            if (a) return `${this._callable(this._expr(a))}.${method}(${rest.join(", ")})`;
            return `${method}(${rest.join(", ")})`;
        }

        // ConvertNode → the target type's factory: float(x), vec3(x)…
        const convertTo = r.convertTo;
        if (typeof convertTo === "string" && convertTo && isNodeLike(r.node)) {
            return `${convertTo}(${this._expr(r.node)})`;
        }

        // SplitNode → swizzle property access: expr.xyz
        if (r.isSplitNode && typeof r.components === "string" && isNodeLike(r.node)) {
            return `${this._callable(this._expr(r.node))}.${r.components}`;
        }

        // JoinNode → component constructor by output type: vec3(a, b, c)
        if (t === "JoinNode" && Array.isArray(r.nodes)) {
            const factory = typeof node.nodeType === "string" && node.nodeType ? node.nodeType : "vec4";
            return `${factory}(${r.nodes.filter(isNodeLike).map(n => this._expr(n)).join(", ")})`;
        }

        if (t === "TextureNode" || r.isTextureNode === true) return this._texture(node);
        if (t === "UniformNode" || r.isUniformNode === true) {
            return `uniform(${this._valueLiteral(r.value, node.nodeType ?? undefined, /* bare */ true)})`;
        }
        if (t === "ConstNode" || r.isConstNode === true) return this._valueLiteral(r.value, node.nodeType ?? undefined);
        // Value-carrying leaf whose type/flag names this three version doesn't
        // expose (structural detection, like the graph builder's isInputStructural).
        if (r.isInputNode === true || ("value" in r && "precision" in r)) {
            return this._valueLiteral(r.value, node.nodeType ?? undefined);
        }

        const attr = r.attributeName;
        if (typeof attr === "string" && attr) {
            return attr === "uv" ? "uv()" : `attribute(${JSON.stringify(attr)})`;
        }

        // Reference nodes read a property off an external object per render.
        const refProp = r.property;
        if (typeof refProp === "string" && refProp && t.endsWith("ReferenceNode")) {
            const owner = t.slice(0, -"ReferenceNode".length);
            return `reference(${JSON.stringify(refProp)})${owner ? ` /* on ${owner.toLowerCase()} */` : ""}`;
        }

        // TSL Fn calls: name(args) — the body is a JS closure (not emitted here;
        // step into the node graph to expand it).
        if (r.isShaderCallNodeInternal || t.includes("ShaderCall")) return this._fnCall(node);

        return this._generic(node);
    }

    private _texture(node: NodeLike): string {
        const r = asRecord(node);
        const value = r.value as { isTexture?: boolean; name?: string } | null | undefined;
        const texName = (value?.isTexture && value.name) || "";
        const ref = this._declareExternal(texName || "map", `/* Texture${texName ? ` "${texName}"` : ""} (live on the material) */ null`);
        const uv = isNodeLike(r.uvNode) ? `, ${this._expr(r.uvNode)}` : "";
        return `texture(${ref}${uv})`;
    }

    private _fnCall(node: NodeLike): string {
        const r = asRecord(node);
        const shaderNode = r.shaderNode as { layout?: { name?: unknown }; name?: unknown } | null | undefined;
        const name =
            (typeof shaderNode?.layout?.name === "string" && shaderNode.layout.name) ||
            (typeof shaderNode?.name === "string" && shaderNode.name) ||
            "";
        const raw = r.inputNodes ?? r.rawInputs;
        const args = (Array.isArray(raw) ? raw : []).filter(isNodeLike).map(n => this._expr(n));
        // A NAMED Fn already reads as a call (`remap(x, …)`) — leave it.
        if (name) return `${this._ident(name)}(${args.join(", ")})`;
        // Anonymous Fn: with an expander (Pro) inline the body, which comes back
        // with THIS call's inputs already substituted, so it drops in where the
        // call was. Depth-capped because each level runs the page's closure.
        if (this._expandFn && this._fnDepth < MAX_FN_EXPAND_DEPTH) {
            let body: NodeLike | null = null;
            try { body = this._expandFn(node); } catch { body = null; }
            if (body) {
                this._fnDepth++;
                try { return this._expr(body); }
                catch { /* fall through to the stub below */ }
                finally { this._fnDepth--; }
            }
        }
        const suffix = this._expandFn
            // Expander present but this body couldn't be built (needs lights /
            // clipping / backend context the throwaway builder lacks).
            ? "/* anonymous Fn — body could not be reconstructed */"
            : "/* anonymous Fn — step into it in the node graph */";
        return `fn(${args.join(", ")}) ${suffix}`;
    }

    /** Fallback for unknown node types: `<name>(…children)`; scoped accessor
     *  nodes without children read as their TSL export (positionWorld…). */
    private _generic(node: NodeLike): string {
        const r = asRecord(node);
        const base = lowerFirst(stripNodeSuffix(typeName(node)));
        const children = [...safeChildren(node)].map(c => this._expr(c.child));
        const scope = r.scope;
        if (!children.length && typeof scope === "string" && scope) {
            return `${base}${upperFirst(scope)}`; // e.g. positionWorld, normalView
        }
        // an AttributeNode's meaning IS its name — attribute('uv'), not attribute()
        if (!children.length && typeof r.attributeName === "string" && r.attributeName) {
            return `${base}('${r.attributeName}')`;
        }
        return `${base}(${children.join(", ")})`;
    }
    // #endregion

    // #region literals & naming
    /** `bare`: raw JS literals for contexts that coerce (uniform(2), not uniform(float(2))). */
    private _valueLiteral(value: unknown, nodeType?: string, bare = false): string {
        if (typeof value === "number") {
            if (bare) return formatNumber(value);
            const factory = nodeType === "int" || nodeType === "uint" ? nodeType : "float";
            return `${factory}(${formatNumber(value)})`;
        }
        if (typeof value === "boolean") return bare ? String(value) : `bool(${value})`;
        if (value && typeof value === "object") {
            const v = value as Record<string, unknown> & { getHexString?: () => string };
            if (v.isColor && typeof v.getHexString === "function") {
                try { return `color('#${v.getHexString()}')`; } catch { return "color()"; }
            }
            if (v.isVector2) return `vec2(${fmt(v.x)}, ${fmt(v.y)})`;
            if (v.isVector3) return `vec3(${fmt(v.x)}, ${fmt(v.y)}, ${fmt(v.z)})`;
            if (v.isVector4) return `vec4(${fmt(v.x)}, ${fmt(v.y)}, ${fmt(v.z)}, ${fmt(v.w)})`;
            if (v.isTexture) return `texture(${this._declareExternal((v.name as string) || "map", "/* Texture (live on the material) */ null")})`;
        }
        return "/* value */ null";
    }

    /**
     * Declares `code` as a variable for `node` and returns the name. When the
     * expression is a chain off a previously declared SINGLE-use variable, it
     * folds into a mutation of that variable instead (the common TSL style):
     *
     *     let alpha = fresnel.remap(0.73, 1, 1, 0).pow(3);
     *     alpha = alpha.mul(sunOrientation.smoothstep(-0.5, 1));
     */
    private _declare(node: NodeLike, code: string): string {
        const chainBase = /^([A-Za-z_$][\w$]*)\./.exec(code)?.[1];
        if (chainBase && this._foldable.has(chainBase)) {
            const baseUuid = this._varUuids.get(chainBase);
            // Single consumer = nobody else reads the base's pre-mutation value.
            if (baseUuid && (this._refs.get(baseUuid) ?? 0) === 1) {
                const declIndex = this._declIndex.get(chainBase);
                if (declIndex !== undefined) {
                    this.declarations[declIndex] = this.declarations[declIndex].replace(/^const /, "let ");
                }
                this.declarations.push(`${chainBase} = ${code};`);
                this._varUuids.set(chainBase, node.uuid); // the name now holds THIS node
                return chainBase;
            }
        }
        const name = this._ident(this._inferName(node));
        this._declIndex.set(name, this.declarations.length);
        this._varUuids.set(name, node.uuid);
        if (this._hasChildren(node)) this._foldable.add(name);
        this.declarations.push(`const ${name} = ${code};`);
        return name;
    }

    /**
     * A variable name inferred from how the node is USED, walking up the
     * single-consumer chain it is the base of (the chain base carries the name
     * of what the chain becomes): the material slot it feeds ("colorNode" →
     * "color"), or "alpha" for the last scalar of a `vec4(…)`. Falls back to
     * the node's own name, then a generic "u"/"v".
     */
    private _inferName(node: NodeLike): string {
        // An authored name always wins over usage inference.
        if (typeof node.name === "string" && node.name) return node.name;
        let current = node;
        for (let i = 0; i < MAX_NAME_SEARCH_DEPTH; i++) {
            const rootKey = this._rootRoles.get(current.uuid);
            if (rootKey) return ROOT_NAME_ALIASES[rootKey] ?? stripNodeSuffix(rootKey).toLowerCase();
            const parents = this._parents.get(current.uuid) ?? [];
            if (parents.length !== 1) break;
            const { parent, property } = parents[0];
            // vec4(x, y, z, <this>) — the trailing scalar is conventionally alpha.
            if (property === "nodes[3]" && parent.nodeType === "vec4") return "alpha";
            // The name flows DOWN a chain to its base: only when this node is the
            // chain base (first operand), single-use, and itself an expression
            // (a bare uniform reads better as "u" than as its chain's name).
            const isChainBase = property === "aNode" && (this._refs.get(current.uuid) ?? 0) === 1;
            if (!isChainBase || !this._hasChildren(current)) break;
            current = this._unwrapAnonymousVar(parent);
        }
        const r = asRecord(node);
        if (r.isUniformNode === true || typeName(node) === "UniformNode") return "u";
        return "v";
    }

    /** Declares a value that only exists live on the page (e.g. a texture). */
    private _declareExternal(base: string, comment: string): string {
        const name = this._ident(base);
        this.declarations.push(`const ${name} = ${comment};`);
        return name;
    }

    /** A unique, safe identifier derived from `base` ("my tex 2" → myTex2_1). */
    private _ident(base: string): string {
        let name = base.replace(/[^\w$]+(\w?)/g, (_, c: string) => c.toUpperCase()).replace(/^\d/, "_$&") || "v";
        if (this._usedNames.has(name)) {
            let i = 1;
            while (this._usedNames.has(`${name}_${i}`)) i++;
            name = `${name}_${i}`;
        }
        this._usedNames.add(name);
        return name;
    }

    /** Wraps an expression so a chained method call parses (literals/negations). */
    private _callable(expr: string): string {
        return /^[\w$]+(\(.*\))?(\.[\w$]+(\(.*\))?)*$/.test(expr) ? expr : `(${expr})`;
    }
    // #endregion
}

/** Child iteration that never throws (host getters can be hostile). */
function* safeChildren(node: NodeLike): Iterable<{ property: string; child: NodeLike }> {
    try {
        yield* getChildNodes(node);
    } catch { /* stop at the offending property */ }
}

/** Last node-valued entry in a stack's statement list (its return value). */
function lastNodeIn(nodes: unknown): NodeLike | null {
    if (!Array.isArray(nodes)) return null;
    for (let i = nodes.length - 1; i >= 0; i--) {
        if (isNodeLike(nodes[i])) return nodes[i] as NodeLike;
    }
    return null;
}

const fmt = (n: unknown): string => (typeof n === "number" ? formatNumber(n) : String(n));

function formatNumber(n: number): string {
    if (Number.isInteger(n)) return String(n);
    return n.toFixed(4).replace(/\.?0+$/, "");
}

function stripNodeSuffix(name: string): string {
    const base = name.endsWith("Node") ? name.slice(0, -4) : name;
    return base.length ? base : name;
}

const lowerFirst = (s: string): string => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
const upperFirst = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
